/**
 * HelpSplitSection — Section 6 of the homepage.
 *
 * A high-contrast "I Need Help / I Can Help" split block that turns
 * the abstract "join the movement" pitch into something actionable.
 *
 * Data sources (live):
 *   • needs  → /api/home/help-split → db.help_requests (+ causes fallback)
 *   • offers → /api/home/help-split → db.opportunities (+ orgs fallback)
 *
 * Design: two side-by-side panels with contrasting colour tokens
 * (soft-blush for needs, soft-mint for offers) sharing a divider.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, HeartHandshake, ArrowRight, MapPin } from "lucide-react";
import { httpPublic } from "@/lib/api";
import { useGeo } from "@/lib/useGeo";

function placeSlug(geo) {
  const city = geo?.city;
  if (!city) return "";
  return city.toLowerCase().replace(/\s+/g, "-");
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function HelpSplitSection() {
  const { geo } = useGeo();
  const [needs, setNeeds] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const place = useMemo(() => placeSlug(geo), [geo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 4 };
    if (place) params.place = place;
    httpPublic
      .get("/home/help-split", { params })
      .then((r) => {
        if (cancelled) return;
        setNeeds(Array.isArray(r.data?.needs) ? r.data.needs : []);
        setOffers(Array.isArray(r.data?.offers) ? r.data.offers : []);
      })
      .catch(() => {
        if (cancelled) return;
        setNeeds([]);
        setOffers([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [place]);

  return (
    <section
      className="w-full"
      style={{ background: "#FDFBF7" }}
      data-testid="home-help-split"
    >
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-16">
        <header className="text-center max-w-2xl mx-auto mb-10">
          <div className="uppercase text-xs font-mono font-semibold tracking-[0.2em] text-[#50586C] mb-3">
            Find or Offer Help
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1226] tracking-tight leading-tight">
            Someone near you needs a hand.
            <br />
            <span className="text-[#1E64C8]">Someone else has one ready.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#50586C] leading-relaxed">
            Real people. Real requests. Real neighbours stepping up — right around{" "}
            <span className="inline-flex items-center gap-1 font-semibold text-[#0B1226]">
              <MapPin className="w-3.5 h-3.5 text-[#1E64C8]" />
              {geo?.city ? `${geo.city}${geo.country ? ", " + geo.country : ""}` : "your community"}
            </span>.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* I NEED HELP */}
          <SplitPanel
            tone="needs"
            icon={HandHeart}
            eyebrow="I Need Help"
            title="Ask for a hand."
            blurb="Post what you need — housing, medical, education, food, transport. Kind neighbours reply within hours."
            ctaLabel="Post a request"
            ctaTo="/request-help"
            secondaryLabel="Browse open requests"
            secondaryTo="/find-help"
            items={needs}
            loading={loading}
            testId="help-panel-needs"
          />

          {/* I CAN HELP */}
          <SplitPanel
            tone="offers"
            icon={HeartHandshake}
            eyebrow="I Can Help"
            title="Show up for someone."
            blurb="Offer time, skills, or resources. Every hour is verified — build Social Capital that recruiters actually see."
            ctaLabel="Offer help"
            ctaTo="/offer-help"
            secondaryLabel="Browse opportunities"
            secondaryTo="/opportunities"
            items={offers}
            loading={loading}
            testId="help-panel-offers"
          />
        </div>
      </div>
    </section>
  );
}

function SplitPanel({
  tone, icon: Icon, eyebrow, title, blurb, ctaLabel, ctaTo,
  secondaryLabel, secondaryTo, items, loading, testId,
}) {
  const isNeeds = tone === "needs";
  const bg   = isNeeds ? "#FFF4EE" : "#EFFAF3";
  const accent = isNeeds ? "#B25D38" : "#0E7C4E";
  const btnBg  = isNeeds ? "#F58220" : "#0E7C4E";

  return (
    <div
      className="rounded-3xl p-6 lg:p-8 relative overflow-hidden"
      style={{ background: bg }}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center w-11 h-11 rounded-full text-white shrink-0"
          style={{ background: accent }}
        >
          <Icon className="w-5 h-5" />
        </span>
        <div>
          <div className="uppercase text-[11px] font-mono font-semibold tracking-[0.2em]" style={{ color: accent }}>
            {eyebrow}
          </div>
          <h3 className="mt-1 text-2xl sm:text-3xl font-semibold text-[#0B1226] leading-tight">
            {title}
          </h3>
        </div>
      </div>

      <p className="mt-3 text-sm sm:text-base text-[#50586C] leading-relaxed">
        {blurb}
      </p>

      {/* Live cards */}
      <ul className="mt-6 space-y-3" data-testid={`${testId}-list`}>
        {loading &&
          ["1", "2", "3"].map((k) => (
            <li key={k} className="animate-pulse rounded-xl bg-white/60 h-16" />
          ))}
        {!loading && items.length === 0 && (
          <li className="rounded-xl bg-white/60 p-4 text-sm text-[#50586C] text-center">
            {isNeeds
              ? "No open requests right now — you could be the first to ask."
              : "No open opportunities yet — be the first to offer help."}
          </li>
        )}
        {!loading && items.slice(0, 3).map((it) => (
          <li key={it.id || it.title}>
            <Link
              to={it.link || "#"}
              className="group flex items-start gap-3 rounded-xl bg-white/95 p-3.5 hover:bg-white hover:shadow-sm transition-all"
              data-testid={`${testId}-item-${it.id}`}
            >
              <span
                className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-[10px] font-bold"
                style={{ background: accent }}
                aria-hidden
              >
                {(it.chip || "•").slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#0B1226] leading-snug line-clamp-1">
                  {it.title}
                </div>
                <div className="mt-0.5 text-xs text-[#50586C] flex items-center gap-2 flex-wrap">
                  {it.chip && <span className="font-semibold">{it.chip}</span>}
                  {it.location && (
                    <span className="inline-flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" /> {it.location}
                    </span>
                  )}
                  {(it.supporters > 0) && (
                    <span>· {it.supporters} supporters</span>
                  )}
                  {(it.hours) && <span>· {it.hours}h</span>}
                  {it.created_at && <span>· {timeAgo(it.created_at)}</span>}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#50586C] group-hover:translate-x-0.5 transition-transform mt-1" />
            </Link>
          </li>
        ))}
      </ul>

      {/* CTAs */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to={ctaTo}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all"
          style={{ background: btnBg }}
          data-testid={`${testId}-cta`}
        >
          {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to={secondaryTo}
          className="inline-flex items-center gap-1 text-sm font-semibold hover:underline underline-offset-4"
          style={{ color: accent }}
          data-testid={`${testId}-secondary`}
        >
          {secondaryLabel} →
        </Link>
      </div>
    </div>
  );
}
